import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, Linkedin } from "lucide-react";
import { FIRM, PRACTICE_AREAS_NAV, CITIES } from "@/constants/firmInfo";
import { useLanguage } from "@/i18n/LanguageContext";
import { PRACTICE_AREAS_ES } from "@/i18n/translations";

export const Footer = () => {
  const year = new Date().getFullYear();
  const { t, isSpanish } = useLanguage();
  const practiceAreaTitle = (area) => (isSpanish ? PRACTICE_AREAS_ES[area.slug]?.title || area.title : area.title);

  return (
    <footer className="bg-[color:var(--navy-950)] text-slate-300 border-t border-[color:var(--gold-500)]/20" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <Link to="/" className="flex items-center gap-3 mb-4">
            <img src={FIRM.logoSeal} alt={`${FIRM.name} seal`} className="h-12 w-12 object-contain" />
            <span className="font-heading text-white text-lg leading-tight">RINNOU &amp; ASSOCIATES</span>
          </Link>
          <p className="text-sm text-slate-400 leading-relaxed max-w-xs" data-testid="footer-tagline">
            {t("footer.tagline")} {FIRM.foundedYear}.
          </p>
          <div className="flex items-center gap-3 mt-5">
            <a href={FIRM.socials.facebook} aria-label="Facebook" className="h-9 w-9 grid place-items-center rounded-full border border-white/15 hover:border-[color:var(--gold-500)] hover:text-[color:var(--gold-500)] transition-colors" data-testid="footer-social-facebook">
              <Facebook className="h-4 w-4" />
            </a>
            <a href={FIRM.socials.linkedin} aria-label="LinkedIn" className="h-9 w-9 grid place-items-center rounded-full border border-white/15 hover:border-[color:var(--gold-500)] hover:text-[color:var(--gold-500)] transition-colors" data-testid="footer-social-linkedin">
              <Linkedin className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div>
          <h3 className="text-white font-heading text-sm tracking-widest uppercase mb-4">{t("footer.practiceAreas")}</h3>
          <ul className="space-y-2 text-sm">
            {PRACTICE_AREAS_NAV.map((area) => (
              <li key={area.slug}>
                <Link to={`/practice-areas/${area.slug}`} className="hover:text-[color:var(--gold-400)] transition-colors" data-testid={`footer-practice-area-${area.slug}`}>
                  {practiceAreaTitle(area)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-white font-heading text-sm tracking-widest uppercase mb-4">{t("footer.firm")}</h3>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-[color:var(--gold-400)] transition-colors">{t("footer.aboutUs")}</Link></li>
            <li><Link to="/case-results" className="hover:text-[color:var(--gold-400)] transition-colors">{t("footer.caseResults")}</Link></li>
            <li><Link to="/insights" className="hover:text-[color:var(--gold-400)] transition-colors">{t("footer.legalInsights")}</Link></li>
            <li><Link to="/contact" className="hover:text-[color:var(--gold-400)] transition-colors">{t("footer.contactLink")}</Link></li>
            <li><Link to="/privacy" className="hover:text-[color:var(--gold-400)] transition-colors" data-testid="footer-privacy-link">{t("footer.privacy")}</Link></li>
            <li><Link to="/terms" className="hover:text-[color:var(--gold-400)] transition-colors" data-testid="footer-terms-link">{t("footer.terms")}</Link></li>
            <li><Link to="/attorney-advertising" className="hover:text-[color:var(--gold-400)] transition-colors" data-testid="footer-attorney-advertising-link">{t("footer.attorneyAdvertising")}</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="text-white font-heading text-sm tracking-widest uppercase mb-4">{t("footer.contact")}</h3>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-[color:var(--gold-500)] shrink-0" />
              <span>{FIRM.addressLine1}<br />{FIRM.city}, {FIRM.state} {FIRM.zip}</span>
            </li>
            <li className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-[color:var(--gold-500)] shrink-0" />
              <a href={`tel:${FIRM.phoneTel}`} className="hover:text-[color:var(--gold-400)]" data-testid="footer-phone-link">{FIRM.phoneDisplay}</a>
            </li>
            <li className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-[color:var(--gold-500)] shrink-0" />
              <a href={`mailto:${FIRM.email}`} className="hover:text-[color:var(--gold-400)]" data-testid="footer-email-link">{FIRM.email}</a>
            </li>
            <li className="flex items-start gap-2">
              <Clock className="h-4 w-4 mt-0.5 text-[color:var(--gold-500)] shrink-0" />
              <span data-testid="footer-office-hours">{FIRM.hours}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-slate-500">
          <span className="uppercase tracking-wide text-slate-500 font-medium">{t("footer.serviceAreas")}:</span>
          {CITIES.map((c, idx) => (
            <span key={c.slug} className="flex items-center gap-2">
              <Link to={`/locations/${c.slug}`} className="hover:text-[color:var(--gold-400)] transition-colors" data-testid={`footer-location-${c.slug}`}>
                {c.name}
              </Link>
              {idx < CITIES.length - 1 && <span className="text-slate-700">&middot;</span>}
            </span>
          ))}
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-xs text-slate-500 leading-relaxed space-y-2">
          <p>
            <strong className="text-slate-400">{t("footer.disclaimerLabel")}</strong> {t("footer.disclaimer")}
          </p>
          <p>&copy; {year} {FIRM.name}. {t("footer.rights")}</p>
        </div>
      </div>
    </footer>
  );
};
